import { ThemeEvents, CartUpdateEvent } from "@theme/events";
import { fetchConfig } from "@theme/utilities";
import { morphSection } from "@theme/section-renderer";

class FreeShakerGoal extends HTMLElement {
  #syncing = false;
  #unlocked = false;
  #confettiInstance = null;
  #lastPercent = 0;
  #lastRemainingCents = 0;
  #lastIsUnlocked = false;

  connectedCallback() {
    this.variantId = Number(this.dataset.variantId);
    this.goalAmount = Number(this.dataset.goalAmount);

    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
    this.refresh();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
  }

  handleCartUpdate = (event) => {
    // Even if this component triggered the update, we still need to
    // re-apply the display after a morph wipes the inner HTML.
    this.refresh();
  };

  get textEl() {
    return this.querySelector('.free-shaker-goal__text');
  }

  get fillEl() {
    return this.querySelector('.free-shaker-goal__bar-fill');
  }

  get canvasEl() {
    return this.querySelector('.free-shaker-goal__confetti-canvas');
  }

    async refresh() {
    if (this.#syncing) return;
    this.#syncing = true;

    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();

      // Only treat as "our" free gift line if it has the _free_gift property.
      // A manually added shaker bottle (no property) is left completely alone.
      const freeGiftItem = cart.items.find(
        (item) => item.variant_id === this.variantId && item.properties && item.properties._free_gift === 'true'
      );

      // Total excluding only the free-gift line (manual shaker purchases still count towards the goal).
      const totalExcludingFreeGift = freeGiftItem
        ? cart.total_price - freeGiftItem.original_line_price
        : cart.total_price;

      const goalCents = this.goalAmount * 100;
      const percent = Math.min(100, Math.round((totalExcludingFreeGift / goalCents) * 100));
      const remainingCents = Math.max(0, goalCents - totalExcludingFreeGift);
      const isUnlocked = totalExcludingFreeGift >= goalCents;

      this.#lastPercent = percent;
      this.#lastRemainingCents = remainingCents;
      this.#lastIsUnlocked = isUnlocked;

      this.updateDisplay(percent, remainingCents, isUnlocked);

      if (isUnlocked && !freeGiftItem) {
        await this.addShaker();
        this.launchConfetti();
      } else if (!isUnlocked && freeGiftItem) {
        this.#unlocked = false;
        await this.removeShaker(freeGiftItem.key);
      }

      // Re-apply display in case a morph just reset the markup
      this.updateDisplay(this.#lastPercent, this.#lastRemainingCents, this.#lastIsUnlocked);
    } catch (error) {
      console.error('Free shaker goal error', error);
    } finally {
      this.#syncing = false;
    }
  }

  updateDisplay(percent, remainingCents, isUnlocked) {
    const fillEl = this.fillEl;
    const textEl = this.textEl;

    if (fillEl) fillEl.style.width = percent + '%';
    if (!textEl) return;

    if (isUnlocked) {
      textEl.innerHTML = "\uD83C\uDF89 You've unlocked a <strong>FREE Shaker Bottle</strong>!";
    } else {
      const remainingFormatted = (remainingCents / 100).toLocaleString('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
      });
      textEl.innerHTML = `Add <strong>${remainingFormatted}</strong> more to get a <strong>FREE Shaker Bottle</strong>!`;
    }
  }

  async addShaker() {
    if (this.#unlocked) return;
    this.#unlocked = true;

    const sectionsToUpdate = this.getSectionsToUpdate();
    const body = JSON.stringify({
      items: [{ id: this.variantId, quantity: 1, properties: { _free_gift: 'true' } }],
      sections: sectionsToUpdate.join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch('/cart/add.js', fetchConfig('json', { body }));
    const data = await response.json();
    this.updateSections(data.sections, sectionsToUpdate);
  }

  async removeShaker(key) {
    const sectionsToUpdate = this.getSectionsToUpdate();
    const body = JSON.stringify({
      updates: { [key]: 0 },
      sections: sectionsToUpdate.join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch('/cart/update.js', fetchConfig('json', { body }));
    const data = await response.json();
    this.updateSections(data.sections, sectionsToUpdate);
  }

  getSectionsToUpdate() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        ids.add(item.dataset.sectionId);
      }
    });
    return Array.from(ids);
  }

  updateSections(sections, sectionIds) {
    if (!sections) return;
    sectionIds.forEach((id) => {
      if (sections[id]) morphSection(id, sections[id]);
    });
    // Re-apply our text/bar immediately after the morph wipes it
    this.updateDisplay(this.#lastPercent, this.#lastRemainingCents, this.#lastIsUnlocked);
  }

   launchConfetti() {
    const canvasEl = this.canvasEl;
    if (typeof window.confetti !== 'function' || !canvasEl) return;

    this.#confettiInstance = window.confetti.create(canvasEl, {
      resize: true,
      useWorker: false,
    });

    const colors = ['#FF6B6B', '#4ECDC4', '#FFD93D', '#A78BFA', '#FF9F45', '#38BDF8', '#F472B6'];

    this.#confettiInstance({
      particleCount: 150,
      spread: 100,
      startVelocity: 40,
      gravity: 0.9,
      scalar: 0.7,
      ticks: 220,
      origin: { y: 0.3 },
      colors: colors,
    });

    setTimeout(() => {
      this.#confettiInstance({
        particleCount: 60,
        spread: 70,
        startVelocity: 30,
        gravity: 0.9,
        scalar: 0.6,
        ticks: 200,
        angle: 60,
        origin: { x: 0.1, y: 0.4 },
        colors: colors,
      });
      this.#confettiInstance({
        particleCount: 60,
        spread: 70,
        startVelocity: 30,
        gravity: 0.9,
        scalar: 0.6,
        ticks: 200,
        angle: 120,
        origin: { x: 0.9, y: 0.4 },
        colors: colors,
      });
    }, 150);
  }
}

if (!customElements.get('free-shaker-goal')) {
  customElements.define('free-shaker-goal', FreeShakerGoal);
}